import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { UnderConstructionDialog } from "../../components/UnderConstructionDialog";
import PortalPostsFeed from "../../components/PortalPostsFeed";
import DocumentSignOffDialog from "../../components/DocumentSignOffDialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Loader2,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Phone,
  Send,
  Home,
  Settings,
  Sun,
  Moon,
  Palette,
  LogOut,
  User,
  Building2,
  PlusCircle,
  Eye,
  CreditCard,
  FolderArchive,
  Download,
  ShieldAlert,
  Briefcase,
  PenLine,
  CheckCircle2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [messages, setMessages] = useState([]);

  // Phase F — portal documents grouped by category
  const [portalDocs, setPortalDocs] = useState({});
  const [docsLoading, setDocsLoading] = useState(false);
  // Phase F.6 Batch D+ — current user's acknowledgments keyed by doc_id
  const [myAcks, setMyAcks] = useState({});
  const [signOffDoc, setSignOffDoc] = useState(null);
  const [signOffOpen, setSignOffOpen] = useState(false);
  
  // Permissions from portal settings
  const [permissions, setPermissions] = useState({
    can_view_quotes: true,
    can_accept_quotes: true,
    can_view_invoices: true,
    can_request_callback: true,
    can_message: true,
    can_request_quote: true,
  });
  
  // Dialogs
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [callbackDialogOpen, setCallbackDialogOpen] = useState(false);
  const [quoteRequestDialogOpen, setQuoteRequestDialogOpen] = useState(false);
  const [quoteDetailOpen, setQuoteDetailOpen] = useState(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Forms
  const [messageForm, setMessageForm] = useState({ subject: "", message: "" });
  const [callbackForm, setCallbackForm] = useState({ phone: "", preferred_time: "", notes: "" });
  const [quoteRequestForm, setQuoteRequestForm] = useState({
    service_type: "",
    location: "",
    description: "",
    preferred_date: "",
  });
  const [saving, setSaving] = useState(false);

  // User preferences
  const [userPreferences, setUserPreferences] = useState({
    accent_color: "#10b981",
    compact_view: false,
  });
  
  // Payment states
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  // Phase F.3 — Pay Now is currently behind a "LIVE AR processing" gate.
  const [payNowUcOpen, setPayNowUcOpen] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client-portal/dashboard`);
      setDashboardData(response.data);
      if (response.data.permissions) {
        setPermissions(response.data.permissions);
      }
      setQuotes(response.data.quotes || []);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load dashboard");
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/client-portal/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, []);

  // Phase F — Client portal "Documents" tab
  const fetchPortalDocs = async () => {
    setDocsLoading(true);
    try {
      const [docsRes, acksRes] = await Promise.all([
        axios.get(`${API}/portal/my-documents`),
        axios.get(`${API}/client-documents/my-acknowledgments`),
      ]);
      setPortalDocs(docsRes.data?.documents || {});
      setMyAcks(acksRes.data?.by_document || {});
    } catch (error) {
      console.error("Failed to fetch portal documents:", error);
    } finally {
      setDocsLoading(false);
    }
  };

  const openSignOff = (doc) => {
    setSignOffDoc(doc);
    setSignOffOpen(true);
  };

  const onAckSuccess = () => {
    // Refresh acks so badges update without re-fetching docs
    axios.get(`${API}/client-documents/my-acknowledgments`).then((res) => {
      setMyAcks(res.data?.by_document || {});
    });
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/client-documents/${docId}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = filename || "document.pdf"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleTabChange = (val) => {
    setActiveTab(val);
    if (val === "documents") fetchPortalDocs();
  };

  // Phase F.6 Batch D+ — Auto-jump to Documents tab when arriving via magic link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (focusId) {
      setActiveTab("documents");
      fetchPortalDocs();
      // Scroll the focused doc into view after the list renders
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="portal-doc-row-${focusId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-amber-400");
          setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 3500);
        }
      }, 800);
      // Clean the URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchMessages()]);
      // Load user preferences from localStorage
      const prefs = localStorage.getItem(`client_prefs_${user?.id}`);
      if (prefs) {
        setUserPreferences(JSON.parse(prefs));
      }
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchMessages, user?.id]);

  // Apply accent color
  useEffect(() => {
    document.documentElement.style.setProperty('--accent-custom', userPreferences.accent_color);
  }, [userPreferences.accent_color]);

  const savePreferences = (newPrefs) => {
    setUserPreferences(newPrefs);
    localStorage.setItem(`client_prefs_${user?.id}`, JSON.stringify(newPrefs));
    toast.success("Preferences saved");
  };

  const handleAcceptQuote = async (quoteId) => {
    setSaving(true);
    try {
      await axios.post(`${API}/client-portal/quotes/${quoteId}/accept`);
      toast.success("Quote accepted!");
      fetchDashboard();
      setQuoteDetailOpen(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept quote");
    } finally {
      setSaving(false);
    }
  };

  const handleDeclineQuote = async (quoteId, reason = "") => {
    setSaving(true);
    try {
      await axios.post(`${API}/client-portal/quotes/${quoteId}/decline`, null, {
        params: { reason }
      });
      toast.success("Quote declined");
      fetchDashboard();
      setQuoteDetailOpen(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline quote");
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject || !messageForm.message) {
      toast.error("Please fill all fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/client-portal/messages`, null, {
        params: messageForm
      });
      toast.success("Message sent!");
      setMessageDialogOpen(false);
      setMessageForm({ subject: "", message: "" });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestCallback = async (e) => {
    e.preventDefault();
    if (!callbackForm.phone) {
      toast.error("Please provide a phone number");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/client-portal/callback-request`, null, {
        params: callbackForm
      });
      toast.success("Callback request submitted!");
      setCallbackDialogOpen(false);
      setCallbackForm({ phone: "", preferred_time: "", notes: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request callback");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestQuote = async (e) => {
    e.preventDefault();
    if (!quoteRequestForm.service_type || !quoteRequestForm.description) {
      toast.error("Please fill required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/client-portal/quote-request`, null, {
        params: quoteRequestForm
      });
      toast.success("Quote request submitted!");
      setQuoteRequestDialogOpen(false);
      setQuoteRequestForm({ service_type: "", location: "", description: "", preferred_date: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request quote");
    } finally {
      setSaving(false);
    }
  };

  // Payment functions
  const handlePayInvoice = async (invoice) => {
    if (invoice.status === "paid") {
      toast.info("This invoice is already paid");
      return;
    }
    
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/session`, {
        invoice_id: invoice.id,
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.info("Payment status check complete. Please refresh the page to see updates.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your invoice has been paid.");
        setPollingPayment(false);
        // Refresh dashboard data
        fetchDashboard();
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setPollingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPollingPayment(false);
    }
  };

  // Check for payment return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentSuccess = params.get("payment_success");
    const paymentCancelled = params.get("payment_cancelled");

    if (paymentCancelled) {
      toast.info("Payment was cancelled");
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paymentSuccess && sessionId) {
      toast.info("Checking payment status...");
      setPollingPayment(true);
      pollPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const getStatusBadge = (status) => {
    const variants = {
      pending: "outline",
      sent: "default",
      accepted: "default",
      declined: "destructive",
      paid: "default",
      overdue: "destructive",
    };
    const colors = {
      pending: "",
      sent: "bg-blue-500",
      accepted: "bg-emerald-500",
      declined: "bg-red-500",
      paid: "bg-emerald-500",
      overdue: "bg-red-500",
    };
    return (
      <Badge variant={variants[status] || "outline"} className={colors[status]}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const branding = dashboardData?.branding || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary-foreground" />
              </div>
            )}
            <span className="font-heading text-lg font-semibold">
              {branding.company_name || "Client Portal"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l">
              <User className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto p-4 md:p-8 space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Welcome, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your quotes, invoices, and communications
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {permissions.can_request_quote && (
              <Button onClick={() => setQuoteRequestDialogOpen(true)} data-testid="request-quote-btn">
                <PlusCircle className="h-4 w-4 mr-2" />
                Request Quote
              </Button>
            )}
            {permissions.can_message && (
              <Button variant="outline" onClick={() => setMessageDialogOpen(true)} data-testid="send-message-btn">
                <MessageSquare className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            )}
            {permissions.can_request_callback && (
              <Button variant="outline" onClick={() => setCallbackDialogOpen(true)} data-testid="request-callback-btn">
                <Phone className="h-4 w-4 mr-2" />
                Request Callback
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card data-testid="stat-pending-quotes">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Quotes</p>
                  <p className="text-2xl font-bold">{dashboardData?.stats?.pending_quotes || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-accepted-quotes">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Accepted Quotes</p>
                  <p className="text-2xl font-bold">{dashboardData?.stats?.accepted_quotes || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-emerald-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-unpaid-invoices">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unpaid Invoices</p>
                  <p className="text-2xl font-bold">{dashboardData?.stats?.unpaid_invoices || 0}</p>
                </div>
                <DollarSign className="h-8 w-8 text-red-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-messages">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Messages</p>
                  <p className="text-2xl font-bold">{messages.length}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-violet-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="grid w-full grid-cols-6" data-testid="client-portal-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            {permissions.can_view_quotes && (
              <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes</TabsTrigger>
            )}
            {permissions.can_view_invoices && (
              <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
            )}
            {permissions.can_message && (
              <TabsTrigger value="messages" data-testid="tab-communications">Communications</TabsTrigger>
            )}
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Recent Quotes */}
              {permissions.can_view_quotes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Recent Quotes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {quotes.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No quotes yet</p>
                    ) : (
                      <div className="space-y-3">
                        {quotes.slice(0, 5).map((quote) => (
                          <div
                            key={quote.id}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                            onClick={() => setQuoteDetailOpen(quote)}
                            data-testid={`quote-item-${quote.id}`}
                          >
                            <div>
                              <p className="font-medium">{quote.quote_number || `Quote #${quote.id.slice(-6)}`}</p>
                              <p className="text-sm text-muted-foreground">
                                ${quote.total_amount?.toLocaleString() || "0"}
                              </p>
                            </div>
                            {getStatusBadge(quote.status)}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent Invoices */}
              {permissions.can_view_invoices && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Recent Invoices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {invoices.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No invoices yet</p>
                    ) : (
                      <div className="space-y-3">
                        {invoices.slice(0, 5).map((invoice) => (
                          <div
                            key={invoice.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                            data-testid={`invoice-item-${invoice.id}`}
                          >
                            <div>
                              <p className="font-medium">{invoice.invoice_number}</p>
                              <p className="text-sm text-muted-foreground">
                                Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "-"}
                              </p>
                            </div>
                            <div className="text-right flex items-center gap-2">
                              <div>
                                <p className="font-medium">${invoice.total?.toLocaleString() || "0"}</p>
                                {getStatusBadge(invoice.status)}
                              </div>
                              {invoice.status !== "paid" && invoice.balance_due > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => setPayNowUcOpen(true)}
                                  data-testid={`pay-invoice-mini-${invoice.id}`}
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          {permissions.can_view_quotes && (
            <TabsContent value="quotes">
              <Card>
                <CardHeader>
                  <CardTitle>All Quotes</CardTitle>
                  <CardDescription>View and manage your quotes</CardDescription>
                </CardHeader>
                <CardContent>
                  {quotes.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No quotes found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quote #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quotes.map((quote) => (
                          <TableRow key={quote.id}>
                            <TableCell className="font-medium">
                              {quote.quote_number || `#${quote.id.slice(-6)}`}
                            </TableCell>
                            <TableCell>
                              {new Date(quote.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>${quote.total_amount?.toLocaleString() || "0"}</TableCell>
                            <TableCell>{getStatusBadge(quote.status)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setQuoteDetailOpen(quote)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Invoices Tab */}
          {permissions.can_view_invoices && (
            <TabsContent value="invoices">
              <Card>
                <CardHeader>
                  <CardTitle>All Invoices</CardTitle>
                  <CardDescription>View your invoices and payment status</CardDescription>
                </CardHeader>
                <CardContent>
                  {pollingPayment && (
                    <div className="flex items-center justify-center gap-2 p-4 mb-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      <span className="text-blue-600 dark:text-blue-400">Verifying payment...</span>
                    </div>
                  )}
                  {invoices.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No invoices found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Balance</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">
                              {invoice.invoice_number}
                            </TableCell>
                            <TableCell>
                              {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : "-"}
                            </TableCell>
                            <TableCell>
                              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "-"}
                            </TableCell>
                            <TableCell>${invoice.total?.toLocaleString() || "0"}</TableCell>
                            <TableCell className={invoice.balance_due > 0 ? "text-red-600 font-medium" : "text-emerald-600"}>
                              ${invoice.balance_due?.toLocaleString() || "0"}
                            </TableCell>
                            <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                            <TableCell className="text-right">
                              {invoice.status !== "paid" && invoice.balance_due > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => setPayNowUcOpen(true)}
                                  disabled={false}
                                  data-testid={`pay-invoice-${invoice.id}`}
                                >
                                  {processingPayment ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <CreditCard className="h-4 w-4 mr-1" />
                                  )}
                                  Pay Now
                                </Button>
                              )}
                              {invoice.status === "paid" && (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Paid
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Messages Tab */}
          {permissions.can_message && (
            <TabsContent value="messages">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Messages</CardTitle>
                    <CardDescription>Your communication history</CardDescription>
                  </div>
                  <Button onClick={() => setMessageDialogOpen(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    New Message
                  </Button>
                </CardHeader>
                <CardContent>
                  {messages.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No messages yet</p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-4 border rounded-lg ${
                            msg.status === "unread" ? "bg-accent/50" : ""
                          }`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{msg.subject}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(msg.created_at).toLocaleString()}
                              </p>
                            </div>
                            <Badge
                              variant={
                                msg.status === "unread"
                                  ? "default"
                                  : msg.status === "responded"
                                  ? "outline"
                                  : "secondary"
                              }
                            >
                              {msg.status}
                            </Badge>
                          </div>
                          <p className="mt-2 text-sm">{msg.message}</p>
                          {msg.response && (
                            <div className="mt-3 p-3 bg-muted rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Response:</p>
                              <p className="text-sm">{msg.response}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Phase F — Documents Tab */}
          <TabsContent value="documents">
            <Card data-testid="portal-docs-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderArchive className="h-5 w-5" /> Your Documents
                </CardTitle>
                <CardDescription>
                  Contracts, notices, and other paperwork shared with you. Sorted by category.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {docsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (() => {
                  const categoryMeta = {
                    contract: { label: "Contracts", icon: Briefcase },
                    notice: { label: "Notices", icon: ShieldAlert },
                    other: { label: "Other", icon: FolderArchive },
                  };
                  const visibleCategories = ["contract", "notice", "other"].concat(
                    Object.keys(portalDocs).filter((k) => !["quote", "invoice", "contract", "notice", "other"].includes(k))
                  );
                  const hasAny = visibleCategories.some((cat) => (portalDocs[cat] || []).length > 0);
                  if (!hasAny) {
                    return (
                      <p className="text-center text-muted-foreground py-8" data-testid="portal-docs-empty">
                        No documents have been shared with you yet.
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-6">
                      {visibleCategories.map((cat) => {
                        const items = portalDocs[cat] || [];
                        if (items.length === 0) return null;
                        const meta = categoryMeta[cat] || { label: cat.charAt(0).toUpperCase() + cat.slice(1), icon: FolderArchive };
                        const Icon = meta.icon;
                        return (
                          <div key={cat} data-testid={`portal-docs-section-${cat}`}>
                            <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                              <Icon className="h-4 w-4 text-primary" />
                              {meta.label}
                              <Badge variant="outline" className="ml-1">{items.length}</Badge>
                            </div>
                            <div className="space-y-2">
                              {items.map((doc) => {
                                const docAcks = myAcks[doc.id] || [];
                                const hasRequiredAck = doc.requires_action
                                  ? docAcks.some((a) => a.action === doc.requires_action)
                                  : false;
                                const actionLabel = {
                                  read: "Mark Read",
                                  signed: "Sign & Submit",
                                  approved: "Approve",
                                  completed: "Mark Completed",
                                  rejected: "Reject",
                                }[doc.requires_action] || "Acknowledge";
                                return (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/30 transition-colors"
                                  data-testid={`portal-doc-row-${doc.id}`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm truncate flex items-center gap-2">
                                      <span>{doc.file_name}</span>
                                      {doc.requires_action && !hasRequiredAck && (
                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300" data-testid={`doc-action-required-${doc.id}`}>
                                          Action required
                                        </Badge>
                                      )}
                                      {doc.requires_action && hasRequiredAck && (
                                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`doc-ack-done-${doc.id}`}>
                                          <CheckCircle2 className="h-3 w-3 mr-1" />
                                          {doc.requires_action}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(doc.created_at).toLocaleDateString()}
                                      {doc.note ? ` • ${doc.note}` : ""}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {doc.requires_action && !hasRequiredAck && (
                                      <Button
                                        size="sm"
                                        onClick={() => openSignOff(doc)}
                                        data-testid={`portal-doc-action-${doc.id}`}
                                      >
                                        <PenLine className="h-3.5 w-3.5 mr-1" />
                                        {actionLabel}
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDownloadDoc(doc.id, doc.file_name)}
                                      data-testid={`portal-doc-download-${doc.id}`}
                                    >
                                      <Download className="h-3.5 w-3.5 mr-1" /> Download
                                    </Button>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Phase F.3 — Posts (notice board) */}
          <TabsContent value="posts">
            <PortalPostsFeed />
          </TabsContent>
        </Tabs>

        {/* Phase F.3 — Pay Now placeholder until LIVE AR add-on launches */}
        <UnderConstructionDialog
          open={payNowUcOpen}
          onOpenChange={setPayNowUcOpen}
          featureName="Pay Now (LIVE AR Processing)"
        />
      </main>

      {/* Phase F.6 Batch D+ — Document Sign-off Dialog */}
      <DocumentSignOffDialog
        open={signOffOpen}
        onOpenChange={(o) => { setSignOffOpen(o); if (!o) setSignOffDoc(null); }}
        doc={signOffDoc}
        currentUserName={user?.name || ""}
        onSuccess={onAckSuccess}
      />

      {/* Quote Detail Dialog */}
      <Dialog open={!!quoteDetailOpen} onOpenChange={() => setQuoteDetailOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
            <DialogDescription>
              {quoteDetailOpen?.quote_number || `Quote #${quoteDetailOpen?.id?.slice(-6)}`}
            </DialogDescription>
          </DialogHeader>
          {quoteDetailOpen && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {new Date(quoteDetailOpen.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div>{getStatusBadge(quoteDetailOpen.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="font-medium text-lg">
                    ${quoteDetailOpen.total_amount?.toLocaleString() || "0"}
                  </p>
                </div>
              </div>

              {quoteDetailOpen.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm">{quoteDetailOpen.description}</p>
                </div>
              )}

              {quoteDetailOpen.services && quoteDetailOpen.services.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Services</p>
                  <div className="space-y-2">
                    {quoteDetailOpen.services.map((service, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center p-2 border rounded"
                      >
                        <span>{service.name}</span>
                        <span className="font-medium">
                          ${service.price?.toLocaleString() || "0"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {permissions.can_accept_quotes && quoteDetailOpen.status === "sent" && (
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleDeclineQuote(quoteDetailOpen.id)}
                    disabled={saving}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={() => handleAcceptQuote(quoteDetailOpen.id)}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Accept Quote
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
            <DialogDescription>Send a message to our team</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={messageForm.subject}
                onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                placeholder="What is this about?"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                placeholder="Type your message..."
                rows={4}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMessageDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Send
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Callback Dialog */}
      <Dialog open={callbackDialogOpen} onOpenChange={setCallbackDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Callback</DialogTitle>
            <DialogDescription>We&apos;ll call you back at your convenience</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestCallback} className="space-y-4">
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={callbackForm.phone}
                onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                placeholder="Your phone number"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Preferred Time</Label>
              <Select
                value={callbackForm.preferred_time}
                onValueChange={(v) => setCallbackForm({ ...callbackForm, preferred_time: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a time..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                  <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                  <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                  <SelectItem value="anytime">Anytime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={callbackForm.notes}
                onChange={(e) => setCallbackForm({ ...callbackForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCallbackDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Request Callback
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quote Request Dialog */}
      <Dialog open={quoteRequestDialogOpen} onOpenChange={setQuoteRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a Quote</DialogTitle>
            <DialogDescription>Tell us what you need</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestQuote} className="space-y-4">
            <div className="space-y-2">
              <Label>Service Type</Label>
              <Input
                value={quoteRequestForm.service_type}
                onChange={(e) => setQuoteRequestForm({ ...quoteRequestForm, service_type: e.target.value })}
                placeholder="e.g., Window Cleaning, Building Maintenance"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Location / Address</Label>
              <Input
                value={quoteRequestForm.location}
                onChange={(e) => setQuoteRequestForm({ ...quoteRequestForm, location: e.target.value })}
                placeholder="Where is the service needed?"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={quoteRequestForm.description}
                onChange={(e) => setQuoteRequestForm({ ...quoteRequestForm, description: e.target.value })}
                placeholder="Describe what you need..."
                rows={4}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Preferred Date (Optional)</Label>
              <Input
                type="date"
                value={quoteRequestForm.preferred_date}
                onChange={(e) => setQuoteRequestForm({ ...quoteRequestForm, preferred_date: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuoteRequestDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preferences</DialogTitle>
            <DialogDescription>Customize your portal experience</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Theme</Label>
              <div className="flex gap-2">
                <Button
                  variant={theme === "light" ? "default" : "outline"}
                  onClick={() => setTheme("light")}
                  className="flex-1"
                >
                  <Sun className="h-4 w-4 mr-2" />
                  Light
                </Button>
                <Button
                  variant={theme === "dark" ? "default" : "outline"}
                  onClick={() => setTheme("dark")}
                  className="flex-1"
                >
                  <Moon className="h-4 w-4 mr-2" />
                  Dark
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Accent Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={userPreferences.accent_color}
                  onChange={(e) =>
                    savePreferences({ ...userPreferences, accent_color: e.target.value })
                  }
                  className="w-16 h-10 p-1"
                />
                <div className="flex gap-1">
                  {["#10b981", "#6366f1", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"].map((color) => (
                    <button
                      key={color}
                      className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110"
                      style={{
                        backgroundColor: color,
                        borderColor: userPreferences.accent_color === color ? "white" : color,
                      }}
                      onClick={() => savePreferences({ ...userPreferences, accent_color: color })}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
